import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { default as Button } from "../../../../../components/common/button/Button";
import { setColor } from "../../../../../assets/styles/styles";
import Modal from "../../../components/Modal";
import JsonViewerModal from "./JsonViewerModal";
import { createVanityLink, fetchAllAvatarPresets } from "../../../../../api/VanityLinkController";
import {
    VANITY_STATIC_URL_DEV,
    VANITY_STATIC_URL_STAGING,
    VANITY_STATIC_URL_PROD,
    CACHE_QUERY_OPTIONS,
    CONFIG_APP_ID,
} from "../../../../../constants";

const getStaticUrl = () => {
    switch (process.env.REACT_APP_STREAM_API_ENV) {
        case "development":
            return VANITY_STATIC_URL_DEV;
        case "staging":
            return VANITY_STATIC_URL_STAGING;
        case "production":
            return VANITY_STATIC_URL_PROD;
        default:
            return VANITY_STATIC_URL_DEV;
    }
};

const CreateVanityLink = ({
    roomId,
    roomSettingsId,
    setStepNext,
    setVanityId,
    setSuccessMessageBanner,
    setBannerType,
    setClientName,
}) => {
    const userId = useSelector((state) => state?.user?.userId);
    const [vanityLinkName, setVanityLinkName] = useState("");
    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");
    const [freeFormJson, setFreeFormJson] = useState("");
    const [staticUrl, setStaticUrl] = useState(getStaticUrl());
    const [applicationId, setApplicationId] = useState(CONFIG_APP_ID.STREAM_APP_ID);
    const [selectedAvatars, setSelectedAvatars] = useState([]);

    const toggleAvatarSelection = (avatarUrl) => {
        setSelectedAvatars(
            (prevSelected) =>
                prevSelected.includes(avatarUrl)
                    ? prevSelected.filter((url) => url !== avatarUrl) // Remove if already selected
                    : [...prevSelected, avatarUrl] // Add if not selected
        );
    };

    const {
        data: avatarPresets,
        error: errPresets,
        isLoading: avatarPresetsLoading,
        refetch,
    } = useQuery(["avatarPresets"], () => fetchAllAvatarPresets(), {
        ...CACHE_QUERY_OPTIONS(),
    });

    const handleJsonSave = async (value, field) => {
        setFreeFormJson(JSON.stringify(value));
    };

    const CreateVanityLinkAPI = async () => {
        const Data = {
            vanityLink: vanityLinkName,
            freeformJson: freeFormJson || {},
            staticUrl,
            applicationId,
            roomId,
            selectedAvatars,
        };

        const response = await createVanityLink(Data);
        setClientName(vanityLinkName);
        if (response?.status === 201) {
            setVanityId(response?.data?.vanityId);
            setBannerType("SUCCESS");
            setSuccessMessageBanner(`Vanity Link Created Successfully.`);
            setStepNext("step4");
        } else if (response?.status === 400) {
            setBannerType("ERROR");
            if (response?.response?.data?.message) {
                setSuccessMessageBanner(`${response?.response?.data?.message}. Vanity Link Name already exists.`);
            } else {
                setSuccessMessageBanner(`Error Creating Vanity Link. Please try again.`);
            }
        } else {
            setBannerType("ERROR");
            setSuccessMessageBanner(`Error Creating Vanity Link. Please try again`);
        }
    };

    useEffect(() => {
        if (avatarPresets && avatarPresets.length > 0 && selectedAvatars.length === 0) {
            const defaultSelected = [
                ...avatarPresets.slice(0, 10).map((a) => a.id),
                ...avatarPresets.slice(11, 13).map((a) => a.id),
            ];
            setSelectedAvatars(defaultSelected);
        }
    }, [avatarPresets]);

    return (
        <>
            <Table>
                <TableRow>
                    <TableColumn>
                        <Label>Room ID</Label>
                        <InputBox type="text" id="roomId" disabled value={roomId} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Room Settings ID</Label>
                        <InputBox type="text" id="roomIdSettings" disabled value={roomSettingsId} />
                    </TableColumn>
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>Vanity Link Name*</Label>
                        <InputBox
                            type="text"
                            id="vanityLinkName"
                            placeholder="Enter vanity Link Name"
                            value={vanityLinkName}
                            onChange={(e) => setVanityLinkName(e.target.value)}
                        />
                    </TableColumn>
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>FreeForm Json</Label>
                        <InputBox
                            type="text"
                            id="freeFormJson"
                            placeholder="Enter FreeForm Json"
                            value={freeFormJson}
                            onClick={() => setIsJsonEditorOpen(true)}
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Label>Static URL</Label>
                        <InputBox
                            type="text"
                            id="staticUrl"
                            placeholder="Enter Static URL"
                            disabled
                            value={staticUrl}
                            onChange={(e) => setStaticUrl(e.target.value)}
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Label>Application ID</Label>
                        <InputBox
                            type="text"
                            id="applicationId"
                            placeholder="Enter Application ID"
                            value={CONFIG_APP_ID.STREAM_APP_ID}
                            disabled
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <AvatarGrid>
                        {!avatarPresetsLoading && avatarPresets?.length > 0 && (
                            <>
                                {avatarPresets.map((item, index) => {
                                    const isSelected = selectedAvatars.includes(item.id);
                                    return (
                                        <AvatarContainer key={index} onClick={() => toggleAvatarSelection(item.id)}>
                                            <StyledImages
                                                src={item.fileUrl}
                                                alt="Avatar Preset"
                                                selected={isSelected}
                                            />
                                            {isSelected && <Checkmark>✔</Checkmark>}
                                        </AvatarContainer>
                                    );
                                })}
                            </>
                        )}
                    </AvatarGrid>
                </TableRow>

                <TableRow>
                    <Button
                        variant={"PinkButton"}
                        buttonWidth={"100%"}
                        disabled={!vanityLinkName}
                        label={"Create Room → Proceed to Step 2"}
                        onClick={CreateVanityLinkAPI}
                    />
                </TableRow>
            </Table>
            {isJsonEditorOpen && (
                <Modal
                    isOpen={isJsonEditorOpen}
                    setIsOpen={setIsJsonEditorOpen}
                    children={
                        <JsonViewerModal
                            jsonEditorValue={jsonEditorValue}
                            handleJsonSave={handleJsonSave}
                            fieldName={"freeFormJson"}
                            label={"FreeForm Json For Vanity Link"}
                            closeModal={() => setIsJsonEditorOpen(false)}
                        />
                    }
                />
            )}
        </>
    );
};

export default CreateVanityLink;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const AvatarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 10px;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
`;

const AvatarContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const StyledImages = styled.img`
    width: 80px;
    height: auto;
    border-radius: 5px;
    margin: 5px;
    cursor: pointer;
    border: ${({ selected }) => (selected ? "3px solid #4CAF50" : "2px solid transparent")};
    transition: border 0.2s ease-in-out;
`;

const Checkmark = styled.div`
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: #4caf50;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
`;
